
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchEvents } from '@/services/eventService';
import { getRegisteredEvents, registerForEvent, unregisterFromEvent } from '@/services/eventRegistrationService';
import { EventWithStatus, EventFilterOptions } from '@/types/event';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export const useEvents = () => {
  const [filteredEvents, setFilteredEvents] = useState<EventWithStatus[]>([]);
  const [filterOptions, setFilterOptions] = useState<EventFilterOptions>({
    searchTerm: '',
    status: 'all',
    location: 'all',
    is_premium: null,
    eventType: 'all'
  });
  
  const [registeredEvents, setRegisteredEvents] = useState<string[]>([]);
  const { isAdmin, user, subscription } = useAuth();

  // 🔥 PERFORMANCE: Use React Query with cache
  const { data: events = [], isLoading, refetch: refreshEvents } = useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      try {
        return await fetchEvents();
      } catch (error) {
        console.error('Error fetching events:', error);
        toast.error('Erreur de chargement', {
          description: 'Impossible de charger les événements.'
        });
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (replaces cacheTime)
    refetchOnWindowFocus: false
  });
  
  const getUserRegistrations = async () => {
    if (user) {
      try {
        const registrations = await getRegisteredEvents(user.id);
        setRegisteredEvents(registrations);
      } catch (error) {
        console.error('Error fetching user registrations:', error);
      }
    }
  };

  // No need for useEffect to fetch - React Query handles it
  
  useEffect(() => {
    if (user) {
      getUserRegistrations();
    }
  }, [user]);

  useEffect(() => {
    if (events.length > 0) {
      let result = [...events];
      
      // Filter by search term
      if (filterOptions.searchTerm) {
        const term = filterOptions.searchTerm.toLowerCase();
        result = result.filter(event => 
          event.title.toLowerCase().includes(term) || 
          (event.description && event.description.toLowerCase().includes(term)) ||
          (event.location && event.location.toLowerCase().includes(term))
        );
      }
      
      // Filter by status
      if (filterOptions.status !== 'all') {
        result = result.filter(event => event.status === filterOptions.status);
      }
      
      // Filter by location
      if (filterOptions.location !== 'all') {
        result = result.filter(event => 
          event.location && event.location.includes(filterOptions.location)
        );
      }
      
      // Filter by premium status
      if (filterOptions.is_premium !== null) {
        result = result.filter(event => event.is_premium === filterOptions.is_premium);
      }
      
    // Filter by event type and VIP access
    if (filterOptions.eventType !== 'all') {
      result = result.filter(event => {
        // Si l'événement est VIP, vérifier l'accès Enterprise
        if (event.event_type === 'vip') {
          return subscription === 'Enterprise';
        }
        return event.event_type === filterOptions.eventType;
      });
    } else {
      // Même sans filtre, masquer les VIP si pas Enterprise
      result = result.filter(event => {
        if (event.event_type === 'vip') {
          return subscription === 'Enterprise';
        }
        return true;
      });
    }
      
      setFilteredEvents(result);
    }
  }, [events, filterOptions]);

  const updateFilters = (newFilters: Partial<EventFilterOptions>) => {
    setFilterOptions(prev => ({ ...prev, ...newFilters }));
  };
  
  const handleEventRegistration = async (event_id: string) => {
    if (!user) {
      toast.error("Connexion requise", {
        description: "Veuillez vous connecter pour vous inscrire à un événement."
      });
      return;
    }
    
    try {
      let success;
      
      // Check if already registered
      if (registeredEvents.includes(event_id)) {
        // Unregister
        success = await unregisterFromEvent(event_id, user.id);
        if (success) {
          setRegisteredEvents(prev => prev.filter(id => id !== event_id));
          toast.success("Désinscription réussie", {
            description: "Vous avez été désinscrit de l'événement."
          });
        }
      } else {
        // Register
        success = await registerForEvent(event_id, user.id);
        if (success) {
          setRegisteredEvents(prev => [...prev, event_id]);
          toast.success("Inscription réussie", {
            description: "Vous avez été inscrit à l'événement."
          });
        }
      }
    } catch (error) {
      console.error('Error handling event registration:', error);
      toast.error("Erreur", {
        description: "Une erreur est survenue lors de l'inscription/désinscription."
      });
    }
  };

  return {
    events: filteredEvents,
    isLoading,
    filterOptions,
    updateFilters,
    refreshEvents,
    isAdmin,
    registeredEvents,
    handleEventRegistration
  };
};
